'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useStore } from '@/hooks/useStore'
import { soundEngine } from '@/utils/soundEngine'
import { FiVolume2, FiVolumeX, FiSun, FiMoon, FiMenu } from 'react-icons/fi'

const navSections = [
  { id: 'home', label: 'Home' },
  { id: 'work', label: 'Why Us?' },
  { id: 'about', label: 'About' },
  { id: 'projects', label: 'Projects' },
  { id: 'contact', label: 'Contact Us' },
]

export default function TopBar() {
  const { activeSection, setActiveSection, isBooted, soundEnabled, toggleSound, theme, toggleTheme, toggleSidebar } = useStore()
  const [hoveredSection, setHoveredSection] = useState<string | null>(null)
  const [hasBootedOnce, setHasBootedOnce] = useState(false)

  // Track once the initial boot staggered entrance and highlight sequence completes
  useEffect(() => {
    if (isBooted && !hasBootedOnce) {
      const timer = setTimeout(() => {
        setHasBootedOnce(true)
      }, 1280) // Triggers exactly when the initial 1.25s green highlight completes
      return () => clearTimeout(timer)
    }
  }, [isBooted, hasBootedOnce])

  const isHome = activeSection === 'home'

  return (
    <header className="fixed top-0 left-0 w-full h-28 z-40 px-6 md:px-12 flex items-center justify-start pointer-events-none">
      {/* Animated Horizontal Glassmorphism Navigation Bar */}
      <div 
        style={{
          transform: !isBooted 
            ? 'translate(0px, -35px) scale(1)' 
            : isHome 
            ? 'translate(0px, 0px) scale(1)' 
            : 'translate(calc(50vw - 57.5% - 3rem), 6px) scale(1.15)',
          opacity: !isBooted ? 0 : 1,
          transformOrigin: 'left center',
          transition: 'transform 1s cubic-bezier(0.65, 0, 0.35, 1), opacity 0.5s ease',
          willChange: 'transform'
        }}
        className="pointer-events-auto flex items-center"
      >
        <nav 
          onMouseLeave={() => setHoveredSection(null)}
          style={{
            background: 'var(--background-image-glass)',
            boxShadow: '0 18px 45px -10px rgba(0, 0, 0, 0.5), inset 0 2px 3px rgba(255, 255, 255, 0.2)',
          }}
          className="flex items-center gap-2 p-2 rounded-full backdrop-blur-[36px] backdrop-saturate-[180%] border border-glass/25 select-none relative"
        >
          {/* Left-Side Sound Mute / Unmute Toggle Button */}
          <motion.button
            onClick={() => {
              toggleSound()
              if (!soundEnabled) soundEngine.playSelect()
            }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            title={soundEnabled ? 'Acoustic Feedback: ON (Click to Mute)' : 'Acoustic Feedback: MUTED (Click to Unmute)'}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 relative z-10 cursor-pointer ${
              soundEnabled
                ? theme === 'light'
                  ? 'bg-black/10 border border-black/30 text-black shadow-sm'
                  : 'bg-brand-green/20 border border-brand-green text-brand-green shadow-[0_0_12px_rgba(163,230,53,0.5)]'
                : 'bg-glass/5 border border-glass/10 text-text-body hover:text-text-title hover:bg-glass/10'
            }`}
          >
            {soundEnabled ? (
              <FiVolume2 className="w-3.5 h-3.5" />
            ) : (
              <FiVolumeX className="w-3.5 h-3.5" />
            )}
          </motion.button>

          {/* Theme Toggle Button */}
          <motion.button
            onClick={() => {
              toggleTheme()
              soundEngine.playHover()
            }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 relative z-10 cursor-pointer bg-glass/5 border border-glass/10 text-text-body hover:text-text-title hover:bg-glass/10"
          >
            {theme === 'dark' ? (
              <FiMoon className="w-3.5 h-3.5" />
            ) : (
              <FiSun className="w-3.5 h-3.5" />
            )}
          </motion.button>

          <div className="w-[1px] h-4 bg-glass/20 my-auto mx-0.5" />

          {/* Mobile-only hamburger — opens the Sidebar drawer as the primary mobile nav */}
          <motion.button
            onClick={() => {
              toggleSidebar()
              soundEngine.playSelect()
            }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            title="Open navigation menu"
            aria-label="Open navigation menu"
            className="md:hidden w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 relative z-10 cursor-pointer bg-glass/5 border border-glass/10 text-text-body hover:text-text-title hover:bg-glass/10"
          >
            <FiMenu className="w-3.5 h-3.5" />
          </motion.button>

          {/* Full section pill row — desktop/tablet only, mobile uses the hamburger + Sidebar */}
          <div className="hidden md:flex items-center gap-2">
            {navSections.map((sec, idx) => {
              const isSelected = activeSection === sec.id
              const isHovered = hoveredSection === sec.id

              return (
                <motion.button
                  key={sec.id}
                  onClick={() => {
                    setActiveSection(sec.id)
                    soundEngine.playWarp()
                  }}
                  onMouseEnter={() => {
                    setHoveredSection(sec.id)
                    soundEngine.playHover()
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.96 }}
                  className={`relative inline-flex items-center justify-center px-6 py-[9px] rounded-full text-[15px] font-normal tracking-wider font-creative cursor-pointer select-none z-10 transition-colors duration-200 ${
                    isSelected
                      ? hasBootedOnce
                        ? 'text-[#060509] font-medium'
                        : 'text-text-title font-normal'
                      : isHovered
                      ? 'text-text-title font-normal'
                      : 'text-text-subtle'
                  }`}
                >
                  {/* Step 3: Active Green Spotlight — 1.25s delay on boot, fluid layoutId slide thereafter */}
                  {isSelected && (
                    <motion.div
                      layoutId="activeNavIndicator"
                      initial={hasBootedOnce ? false : { scale: 0, opacity: 0 }}
                      animate={isBooted ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                      transition={{
                        type: 'tween',
                        duration: 0.38,
                        ease: [0.16, 1, 0.3, 1],
                        delay: hasBootedOnce ? 0 : 1.25,
                      }}
                      className="absolute inset-0 rounded-full bg-brand-green shadow-[0_0_22px_rgba(163,230,53,0.85),_inset_0_1px_2px_rgba(255,255,255,0.4)] -z-10"
                    />
                  )}

                  {/* Smooth Hover Spotlight for Unselected Items */}
                  {isHovered && !isSelected && (
                    <motion.div
                      layoutId="hoverNavIndicator"
                      transition={{ type: 'tween', duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute inset-0 rounded-full bg-glass/[0.12] border border-glass/15 shadow-[inset_0_1px_1px_rgba(255,255,255,0.35)] -z-10"
                    />
                  )}

                  {/* Step 2: Names appear one by one on initial boot, but never re-blur or reload when navigating sections */}
                  <motion.span
                    initial={hasBootedOnce ? false : { opacity: 0, y: 8 }}
                    animate={isBooted ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
                    transition={{ duration: 0.5, delay: hasBootedOnce ? 0 : 0.45 + idx * 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="relative z-10 block leading-none pt-[1.5px] text-center"
                  >
                    {sec.label}
                  </motion.span>
                </motion.button>
              )
            })}
          </div>
        </nav>
      </div>
    </header>
  )
}
